import { db } from './firebase';
import {
  doc, getDoc, setDoc, deleteDoc,
  collection, query, getDocs, startAt, endAt, orderBy,
  runTransaction, onSnapshot
} from 'firebase/firestore';

// ─── Tenant identity ──────────────────────────────────────────────────────────
// Set once at app boot via initStorage(weddingId). When null, all reads/writes
// fall back to the original top-level 'shared' collection for backwards compat.
let _weddingId = null;

export function initStorage(weddingId) {
  _weddingId = weddingId || null;
}

export function getWeddingId() {
  return _weddingId;
}

// Path helpers — always use these instead of raw doc(db, 'shared', ...) literals.
// Fallback to top-level 'shared' when no weddingId (backwards compat with live app).
function sharedRef(key) {
  if (_weddingId) return doc(db, 'weddings', _weddingId, 'shared', key);
  return doc(db, 'shared', key);
}

function sharedCollectionRef() {
  if (_weddingId) return collection(db, 'weddings', _weddingId, 'shared');
  return collection(db, 'shared');
}

// ─── In-memory cache (Layer 2) ────────────────────────────────────────────────
// Short-circuits redundant Firestore SDK calls within the same session.
// All shared writes also update the cache immediately, so the writer always
// sees fresh data. Other clients' caches expire naturally via TTL.
const TTL_MS = {
  leaderboard: 30 * 1000,     // 30 sec — refreshed often during game events
  default:     5 * 60 * 1000, // 5 min  — events, venues, puzzles, game configs
};

function getTTL(key) {
  if (key === 'leaderboard') return TTL_MS.leaderboard;
  return TTL_MS.default;
}

const memCache = new Map(); // key → { value: resultObject, cachedAt: timestamp }

function cacheGet(key) {
  const entry = memCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > getTTL(key)) { memCache.delete(key); return null; }
  return entry.value;
}
function cacheSet(key, value) { memCache.set(key, { value, cachedAt: Date.now() }); }
function cacheDelete(key)      { memCache.delete(key); }

// ─── localStorage key namespacing ────────────────────────────────────────────
// Personal keys (guest-profile, etc.) are scoped per tenant so a guest using
// the same browser across multiple wedding apps doesn't bleed their identity
// from one tenant into another. Root-path (no weddingId) keeps bare keys for
// full backwards compatibility with the original live app.
function localKey(key) {
  return _weddingId ? `${_weddingId}:${key}` : key;
}

// ─── Storage abstraction ──────────────────────────────────────────────────────
export const storage = {
  async get(key, shared = false) {
    try {
      // Personal data: localStorage only
      if (!shared) {
        const value = localStorage.getItem(localKey(key));
        if (value) return { key, value, shared: false };
        return null;
      }

      // Shared data: check in-memory cache first, then Firestore
      const cached = cacheGet(key);
      if (cached !== null) return cached;

      const docRef = sharedRef(key);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const result = { key, value: docSnap.data().value, shared: true };
        cacheSet(key, result);
        return result;
      }
      return null;
    } catch (error) {
      console.error('Storage get error:', error);
      throw error;
    }
  },

  async set(key, value, shared = false) {
    try {
      if (!shared) {
        localStorage.setItem(localKey(key), value);
        return { key, value, shared: false };
      }

      const docRef = sharedRef(key);
      await setDoc(docRef, { value, updatedAt: new Date() });
      const result = { key, value, shared: true };
      cacheSet(key, result); // keep cache in sync on write
      return result;
    } catch (error) {
      console.error('Storage set error:', error);
      throw error;
    }
  },

  async delete(key, shared = false) {
    try {
      if (!shared) {
        localStorage.removeItem(localKey(key));
        return { key, deleted: true, shared: false };
      }

      const docRef = sharedRef(key);
      await deleteDoc(docRef);
      cacheDelete(key);
      return { key, deleted: true, shared: true };
    } catch (error) {
      console.error('Storage delete error:', error);
      throw error;
    }
  },

  async list(prefix = '', shared = false) {
    try {
      if (!shared) {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (!prefix || key.startsWith(prefix)) keys.push(key);
        }
        return { keys, prefix: prefix || undefined, shared: false };
      }

      const collectionRef = sharedCollectionRef();
      let q;
      if (prefix) {
        q = query(collectionRef, orderBy('__name__'), startAt(prefix), endAt(prefix + '\uf8ff'));
      } else {
        q = query(collectionRef);
      }
      const querySnapshot = await getDocs(q);
      const keys = querySnapshot.docs.map(d => d.id);
      return { keys, prefix: prefix || undefined, shared: true };
    } catch (error) {
      console.error('Storage list error:', error);
      throw error;
    }
  }
};

// ─── Award Points (transactional) ────────────────────────────────────────────
// Uses a Firestore transaction to safely increment a player's score in the
// consolidated leaderboard document, avoiding lost-update races when
// multiple players earn points simultaneously.
export async function awardPoints(guestName, pts) {
  const docRef = sharedRef('leaderboard');
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(docRef);
    const scores = snap.exists() ? JSON.parse(snap.data().value) : {};
    scores[guestName] = (scores[guestName] || 0) + pts;
    transaction.set(docRef, { value: JSON.stringify(scores), updatedAt: new Date() });
  });
  cacheDelete('leaderboard'); // force next read to pick up new value
}

// ─── Guest registry (transactional) ──────────────────────────────────────────
// Atomically checks uniqueness and adds a new name to the registry.
// Returns { success: true } or { success: false, reason: 'taken' }.
export async function registerGuestName(fullName) {
  const regRef = sharedRef('guest-registry');
  let taken = false;
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(regRef);
    const names = snap.exists() ? JSON.parse(snap.data().value) : [];
    if (names.map(n => n.toLowerCase()).includes(fullName.toLowerCase())) {
      taken = true;
      return;
    }
    names.push(fullName);
    transaction.set(regRef, { value: JSON.stringify(names), updatedAt: new Date() });
  });
  if (taken) return { success: false, reason: 'taken' };
  return { success: true };
}

// Atomically removes a guest's name from the registry (used on profile reset).
export async function deregisterGuestName(fullName) {
  const regRef = sharedRef('guest-registry');
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(regRef);
    const names = snap.exists() ? JSON.parse(snap.data().value) : [];
    const updated = names.filter(n => n.toLowerCase() !== fullName.toLowerCase());
    transaction.set(regRef, { value: JSON.stringify(updated), updatedAt: new Date() });
  });
}

// Atomically renames a guest in both the registry and the leaderboard.
// Returns { success: true } or { success: false, reason: 'taken' }.
export async function updateGuestName(oldName, newName) {
  const regRef = sharedRef('guest-registry');
  const lbRef  = sharedRef('leaderboard');
  let taken = false;
  await runTransaction(db, async (transaction) => {
    const regSnap = await transaction.get(regRef);
    const lbSnap  = await transaction.get(lbRef);
    const names = regSnap.exists() ? JSON.parse(regSnap.data().value) : [];
    if (names.map(n => n.toLowerCase()).includes(newName.toLowerCase())) {
      taken = true;
      return;
    }
    const updated = names.filter(n => n.toLowerCase() !== oldName.toLowerCase());
    updated.push(newName);
    transaction.set(regRef, { value: JSON.stringify(updated), updatedAt: new Date() });
    const scores = lbSnap.exists() ? JSON.parse(lbSnap.data().value) : {};
    if (scores[oldName] !== undefined) {
      scores[newName] = scores[oldName];
      delete scores[oldName];
      transaction.set(lbRef, { value: JSON.stringify(scores), updatedAt: new Date() });
    }
  });
  cacheDelete('leaderboard');
  if (taken) return { success: false, reason: 'taken' };
  return { success: true };
}

// ─── Sap Links completion (rank-based points) ────────────────────────────────
// Atomically records a puzzle completion and awards tiered points.
// Returns { alreadyDone: true } if the player already completed this puzzle,
// otherwise { alreadyDone: false, rank, ptsAwarded }.
export async function recordSapLinksCompletion(puzzleId, guestName, elapsedSeconds, maxPoints) {
  const compRef = sharedRef(`sap-links-completions-${puzzleId}`);
  const lbRef   = sharedRef('leaderboard');

  let rank, ptsAwarded;

  await runTransaction(db, async (transaction) => {
    const compSnap = await transaction.get(compRef);
    const lbSnap   = await transaction.get(lbRef);

    const completions = compSnap.exists() ? JSON.parse(compSnap.data().value) : {};
    if (completions[guestName]) return; // already recorded — skip

    rank = Object.keys(completions).length + 1; // 1-indexed
    if (rank === 1)      ptsAwarded = maxPoints;
    else if (rank <= 5)  ptsAwarded = Math.max(1, Math.round(maxPoints * 0.6));
    else                 ptsAwarded = Math.max(1, Math.round(maxPoints * 0.3));

    completions[guestName] = { time: elapsedSeconds, completedAt: new Date().toISOString(), rank, ptsAwarded };
    transaction.set(compRef, { value: JSON.stringify(completions), updatedAt: new Date() });

    const scores = lbSnap.exists() ? JSON.parse(lbSnap.data().value) : {};
    scores[guestName] = (scores[guestName] || 0) + ptsAwarded;
    transaction.set(lbRef, { value: JSON.stringify(scores), updatedAt: new Date() });
  });

  cacheDelete('leaderboard');
  if (rank === undefined) return { alreadyDone: true };
  return { alreadyDone: false, rank, ptsAwarded };
}

// ─── Leaderboard live subscription ───────────────────────────────────────────
// Returns an unsubscribe function. Callback receives a plain { name → pts } map.
export function subscribeLeaderboard(callback) {
  const docRef = sharedRef('leaderboard');
  return onSnapshot(docRef, (snap) => {
    if (!snap.exists()) { callback({}); return; }
    try { callback(JSON.parse(snap.data().value)); }
    catch (e) { callback({}); }
  });
}
